"use client";

import { useEffect, useState, useCallback } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { OnboardingTypeform } from "./onboarding-typeform";
import { useAuth } from "@/contexts/AuthContext";
import { API_BASE_URL } from "@/lib/config";
import { useUserUsage } from "@/hooks/useUserUsage";
import dynamic from "next/dynamic";
import {
  ONBOARDING_DEMO_CURATED_ID,
  ONBOARDING_START_QUERY_PARAM,
  isOnboardingStartVisit,
} from "@/lib/onboarding-constants";
import { dispatchStageComplete } from "@/lib/onboarding-tour";
import { telemetry } from "@/lib/telemetry";

const PlansModal = dynamic(() => import("@/components/plans-modal"), { ssr: false });

const SESSION_KEY = "onboarding_status_checked";
const onboardingSessionKey = (uid: string) => `${SESSION_KEY}:${uid}`;
const DISMISS_KEY = "onboarding_questionnaire_dismissed";
const onboardingDismissKey = (uid: string) => `${DISMISS_KEY}:${uid}`;
const DISABLE_LEGACY_ONBOARDING_MODAL = false;

interface OnboardingProgress {
  answers: (string | null)[];
  portfolio_text: string;
  last_completed_stage: number;
}

export function OnboardingManager() {
  const { user, emailVerified } = useAuth();
  const { data: usageData } = useUserUsage();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [savedProgress, setSavedProgress] = useState<OnboardingProgress | null>(null);
  const [showPlansModal, setShowPlansModal] = useState(false);

  useEffect(() => {
    const shouldStartOnboarding = pathname === "/" && isOnboardingStartVisit(searchParams);

    // Gate: must be signed-in AND email-verified
    if (!user?.uid || !emailVerified) {
      setShowOnboarding(false);
      return;
    }

    if (DISABLE_LEGACY_ONBOARDING_MODAL) {
      setShowOnboarding(false);
      return;
    }

    // Never show on subscription page
    if (pathname === "/subscription") {
      setShowOnboarding(false);
      return;
    }

    if (shouldStartOnboarding) {
      localStorage.removeItem(onboardingDismissKey(user.uid));
      setSavedProgress(null);
      setShowOnboarding(true);
      return;
    }

    if (localStorage.getItem(onboardingDismissKey(user.uid)) === "true") {
      setShowOnboarding(false);
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/onboarding/status?user_id=${encodeURIComponent(user.uid)}`
        );
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();

        if (data.completed) {
          localStorage.setItem(onboardingSessionKey(user.uid), "completed");
          setShowOnboarding(false);
          return;
        }

        setSavedProgress(data.progress ?? null);
        setShowOnboarding(true);
      } catch (err) {
        setShowOnboarding(false);
      }
    };

    checkStatus();
  }, [user, emailVerified, pathname, searchParams]);

  // ── Save partial progress (fire-and-forget) ────────────────────────
  const handleProgress = useCallback(
    (stage: number, answers: (string | null)[], portfolioText: string) => {
      if (!user?.uid) return;
      fetch(`${API_BASE_URL}/onboarding/progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.uid,
          stage,
          answers,
          portfolio_text: portfolioText,
        }),
      }).catch(() => {});
    },
    [user]
  );

  // ── Mark completed (reached suggestions slide) ─────────────────────
  const completeOnboarding = useCallback(
    async (
      answers: (string | null)[],
      portfolioText: string,
      promptSelected: string | null,
      promptSubmitted: boolean,
      skipped: boolean
    ) => {
      if (!user?.uid) return;
      try {
        const res = await fetch(`${API_BASE_URL}/onboarding/complete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: user.uid,
            answers,
            portfolio_text: portfolioText,
            prompt_selected: promptSelected,
            prompt_submitted: promptSubmitted,
            skipped,
          }),
        });
        if (!res.ok) throw new Error(`Status ${res.status}`);
        localStorage.setItem(onboardingSessionKey(user.uid), "completed");
        localStorage.removeItem("show_onboarding_survey");
        // Mark the matching tour-hub stage as completed.
        dispatchStageComplete("questionnaire");
      } catch (err) {
      }
    },
    [user]
  );

  // User completed onboarding (questionnaire finished) — trigger video modal
  const handleComplete = useCallback(
    (data: { answers: string[]; portfolioText: string; portfolioImage: File | null; selectedPrompt?: string }) => {
      setShowOnboarding(false);
      // Close session sidebar via cookie/localStorage (we're outside SidebarProvider)
      document.cookie = `sidebar:state=false; path=/; max-age=${60 * 60 * 24 * 7}`;
      localStorage.setItem("sidebar:state", "false");

      completeOnboarding(data.answers, data.portfolioText, null, false, false);
      
      // Trigger video modal to open immediately after questionnaire
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("xynth:open-alerts-onboarding"));
      }, 300);
    },
    [completeOnboarding]
  );

  const handleDismiss = useCallback(
    () => {
      if (user?.uid) {
        localStorage.setItem(onboardingDismissKey(user.uid), "true");
      }
      setShowOnboarding(false);
      // Strip the `?onboarding_start=1` trigger so a refresh / back nav
      // doesn't reopen the questionnaire, and ensure we land cleanly
      // on the home root. The gate (`shouldStartOnboarding`) checks
      // `pathname === "/"` so we're already on home when this fires;
      // a `router.replace("/")` covers both the param-strip and the
      // belt-and-braces "go home" requirement in one call.
      try {
        router.replace("/", { scroll: false });
      } catch {
        // Fallback: history.replaceState if the router fails for
        // any reason (e.g. SSR mismatch during early hydration).
        if (typeof window !== "undefined") {
          const next = new URLSearchParams(searchParams?.toString() || "");
          next.delete(ONBOARDING_START_QUERY_PARAM);
          const qs = next.toString();
          window.history.replaceState(
            window.history.state,
            "",
            "/" + (qs ? `?${qs}` : ""),
          );
        }
      }
    },
    [user?.uid, router, searchParams]
  );

  // User clicked send on the "Let's get started" prompt — close onboarding,
  // mark complete, and route them into the curated onboarding replay. The
  // post-replay click-around tour and pricing CTA will be wired separately.
  const handleOpenPlans = useCallback((data?: { answers: string[]; portfolioText: string; portfolioImage: File | null; selectedPrompt?: string }) => {
    setShowOnboarding(false);
    if (user?.uid) {
      localStorage.setItem(onboardingSessionKey(user.uid), "completed");
      localStorage.removeItem(onboardingDismissKey(user.uid));
    }
    void completeOnboarding(data?.answers ?? [], data?.portfolioText ?? "", null, true, false);
    telemetry.emit("onboarding.demo_replay_route", {
      user_id: user?.uid,
      curated_id: ONBOARDING_DEMO_CURATED_ID,
      answered_count: (data?.answers ?? []).filter(Boolean).length,
      has_portfolio_text: (data?.portfolioText ?? "").length > 0,
    });
    router.push(`/replay/${ONBOARDING_DEMO_CURATED_ID}`);
  }, [completeOnboarding, router, user?.uid]);

  return (
    <>
      <OnboardingTypeform
        open={showOnboarding}
        onComplete={handleComplete}
        onDismiss={handleDismiss}
        onOpenPlans={handleOpenPlans}
        onProgress={handleProgress}
        savedProgress={savedProgress}
      />
      <PlansModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        currentPlan="free"
        stripeAccount={usageData?.stripe_account}
      />
    </>
  );
}
